import React, { type FC, use, useCallback, useEffect, useMemo } from "react";
import { Controller, type ControllerProps, useForm } from "react-hook-form";
import {
    Button,
    Calendar,
    DateField,
    DatePicker,
    FieldError,
    Input,
    Label,
    ListBox,
    Modal,
    Select,
    TextArea,
    TextField,
} from "@heroui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { type CalendarDate, parseDate } from "@internationalized/date";
import { GQL } from "@binale-tech/shared";
import { TrashBin } from "@gravity-ui/icons";

import { CompanyContext } from "@domain/context/CompanyContext";
import { CalendarEventsControlContext, type CalendarEventSaveInput } from "@domain/context/CalendarEventsContext";
import { decodeKey } from "@domain/api/firebase/firebase";
import { useColorItems } from "@ui-components/hooks/useColorItems";
import { PopconfirmHero } from "@ui-components/PopconfirmHero";
import { InputContact } from "@ui-components/Accounting/inputs/InputContact";

type Props = {
    open: boolean;
    onClose: () => void;
    data?: GQL.ICalendarEvent;
    // SP4 (FIBU-22): pre-fills eventDate on CREATE only (ignored when `data`
    // is set - editing always keeps the row's own date) - the dedicated
    // calendar page's "+" on a day cell / agenda-section header pass the
    // clicked/relevant date here (design spec §IV.7 Part B).
    defaultDate?: CalendarDate | null;
};

type FormValues = {
    title: string;
    eventDate: CalendarDate | null;
    type: GQL.ICalendarEventType;
    assigneeId: string;
    contactId: string;
    notes: string;
    color: string;
};

const TYPE_OPTIONS: { value: GQL.ICalendarEventType; labelId: string }[] = [
    { value: GQL.ICalendarEventType.Meeting, labelId: "app.home.calendar.type.meeting" },
    { value: GQL.ICalendarEventType.Appointment, labelId: "app.home.calendar.type.appointment" },
    { value: GQL.ICalendarEventType.Reminder, labelId: "app.home.calendar.type.reminder" },
    { value: GQL.ICalendarEventType.Delivery, labelId: "app.home.calendar.type.delivery" },
    { value: GQL.ICalendarEventType.Other, labelId: "app.home.calendar.type.other" },
];

const EMPTY_VALUES: FormValues = {
    title: "",
    eventDate: null,
    type: GQL.ICalendarEventType.Meeting,
    assigneeId: "",
    contactId: "",
    notes: "",
    color: "",
};

const toFormValues = (data: GQL.ICalendarEvent | undefined, defaultDate?: CalendarDate | null): FormValues =>
    data
        ? {
              title: data.title,
              eventDate: parseDate(data.eventDate),
              type: data.type,
              assigneeId: data.assigneeId ?? "",
              contactId: data.contactId ?? "",
              notes: data.notes ?? "",
              color: data.color ?? "",
          }
        : { ...EMPTY_VALUES, eventDate: defaultDate ?? null };

// Hoisted to module scope (not created per-render) - same pattern as
// RecordTextTemplatesView.tsx's DELETE_CONFIRM_TITLE/DELETE_TRIGGER.
const CONTACT_LABEL = <FormattedMessage id="app.home.calendar.modal.fields.contact" />;
const DELETE_CONFIRM_TITLE = <FormattedMessage id="app.confirmation.header" />;
const DELETE_TRIGGER = (
    <Button variant="danger">
        <TrashBin className="size-4" />
        <FormattedMessage id="app.home.calendar.modal.delete" />
    </Button>
);

const CalendarEventFormModalFC: FC<Props> = ({ open, onClose, data, defaultDate }) => {
    const intl = useIntl();
    const { companyGQL } = use(CompanyContext);
    const { saveEvent, deleteEvent } = use(CalendarEventsControlContext);
    const colorItems = useColorItems();
    const isEdit = Boolean(data);

    const { control, handleSubmit, reset } = useForm<FormValues>({
        defaultValues: toFormValues(data, defaultDate),
    });

    useEffect(() => {
        reset(toFormValues(data, defaultDate));
    }, [data, defaultDate, reset]);

    const requiredMessage = intl.formatMessage({ id: "app.validation.error.field_empty" });
    const titleRules = useMemo(
        () => ({ validate: (v: string) => v.trim().length > 0 || requiredMessage }),
        [requiredMessage],
    );
    const dateRules = useMemo(() => ({ required: requiredMessage }), [requiredMessage]);

    const handleClose = useCallback(() => {
        reset(EMPTY_VALUES);
        onClose();
    }, [reset, onClose]);

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (!isOpen) {
                handleClose();
            }
        },
        [handleClose],
    );

    const onFinish = useCallback(
        (values: FormValues) => {
            const input: CalendarEventSaveInput = {
                id: data?.id,
                title: values.title.trim(),
                eventDate: values.eventDate.toString(),
                type: values.type,
                assigneeId: values.assigneeId || null,
                contactId: values.contactId || null,
                notes: values.notes.trim() || null,
                color: values.color || null,
            };
            saveEvent(input).then((saved) => {
                if (saved) {
                    handleClose();
                }
            });
        },
        [data?.id, saveEvent, handleClose],
    );

    const handleDelete = useCallback(() => {
        if (data?.id) {
            deleteEvent(data.id).then(() => handleClose());
        }
    }, [data?.id, deleteEvent, handleClose]);

    const renderTitle = useCallback<ControllerProps<FormValues, "title">["render"]>(
        ({ field, fieldState }) => (
            <TextField {...field} isInvalid={!!fieldState.error} isRequired fullWidth>
                <Label>
                    <FormattedMessage id="app.home.calendar.modal.fields.title" />
                </Label>
                <Input />
                <FieldError>{fieldState.error?.message}</FieldError>
            </TextField>
        ),
        [],
    );

    const renderEventDate = useCallback<ControllerProps<FormValues, "eventDate">["render"]>(
        ({ field, fieldState }) => (
            <DatePicker
                isRequired
                isInvalid={!!fieldState.error}
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
            >
                <Label>
                    <FormattedMessage id="app.home.calendar.modal.fields.eventDate" />
                </Label>
                <DateField.Group fullWidth>
                    <DateField.Input>{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
                    <DateField.Suffix>
                        <DatePicker.Trigger>
                            <DatePicker.TriggerIndicator />
                        </DatePicker.Trigger>
                    </DateField.Suffix>
                </DateField.Group>
                {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
                <DatePicker.Popover>
                    <Calendar aria-label="Event date">
                        <Calendar.Header>
                            <Calendar.YearPickerTrigger>
                                <Calendar.YearPickerTriggerHeading />
                                <Calendar.YearPickerTriggerIndicator />
                            </Calendar.YearPickerTrigger>
                            <Calendar.NavButton slot="previous" />
                            <Calendar.NavButton slot="next" />
                        </Calendar.Header>
                        <Calendar.Grid>
                            <Calendar.GridHeader>
                                {(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
                            </Calendar.GridHeader>
                            <Calendar.GridBody>{(date) => <Calendar.Cell date={date} />}</Calendar.GridBody>
                        </Calendar.Grid>
                    </Calendar>
                </DatePicker.Popover>
            </DatePicker>
        ),
        [],
    );

    const renderType = useCallback<ControllerProps<FormValues, "type">["render"]>(
        ({ field }) => (
            <Select fullWidth value={field.value} onChange={field.onChange} onBlur={field.onBlur}>
                <Label>
                    <FormattedMessage id="app.home.calendar.modal.fields.type" />
                </Label>
                <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox>
                        {TYPE_OPTIONS.map((opt) => (
                            <ListBox.Item
                                key={opt.value}
                                id={opt.value}
                                textValue={intl.formatMessage({ id: opt.labelId })}
                            >
                                <FormattedMessage id={opt.labelId} />
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                        ))}
                    </ListBox>
                </Select.Popover>
            </Select>
        ),
        [intl],
    );

    const renderAssignee = useCallback<ControllerProps<FormValues, "assigneeId">["render"]>(
        ({ field }) => (
            <Select
                fullWidth
                value={field.value}
                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- plain render helper (not a component); no hook context available, forwards render-time onChange arg
                onChange={(v) => field.onChange(String(v ?? ""))}
                onBlur={field.onBlur}
            >
                <Label>
                    <FormattedMessage id="app.home.calendar.modal.fields.assignee" />
                </Label>
                <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox>
                        <ListBox.Item
                            id=""
                            textValue={intl.formatMessage({ id: "app.home.calendar.modal.fields.assigneeNone" })}
                        >
                            <FormattedMessage id="app.home.calendar.modal.fields.assigneeNone" />
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                        {(companyGQL?.members ?? []).map((member: GQL.ICompanyMember) => {
                            const email = decodeKey(member.emailKey);
                            return (
                                <ListBox.Item key={member.id} id={member.id} textValue={email}>
                                    {email}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            );
                        })}
                    </ListBox>
                </Select.Popover>
            </Select>
        ),
        [companyGQL?.members, intl],
    );

    const renderContact = useCallback<ControllerProps<FormValues, "contactId">["render"]>(
        ({ field }) => (
            <InputContact
                value={field.value || undefined}
                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- plain render helper (not a component); no hook context available, forwards render-time onChange arg
                onChange={(v) => field.onChange(v ?? "")}
                label={CONTACT_LABEL}
            />
        ),
        [],
    );

    const renderNotes = useCallback<ControllerProps<FormValues, "notes">["render"]>(
        ({ field }) => (
            <TextField {...field} fullWidth>
                <Label>
                    <FormattedMessage id="app.home.calendar.modal.fields.notes" />
                </Label>
                <TextArea />
            </TextField>
        ),
        [],
    );

    const renderColor = useCallback<ControllerProps<FormValues, "color">["render"]>(
        ({ field }) => (
            <Select
                fullWidth
                value={field.value}
                // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- plain render helper (not a component); no hook context available, forwards render-time onChange arg
                onChange={(v) => field.onChange(String(v ?? ""))}
                onBlur={field.onBlur}
            >
                <Label>
                    <FormattedMessage id="app.home.calendar.modal.fields.color" />
                </Label>
                <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox>
                        <ListBox.Item id="" textValue={intl.formatMessage({ id: "app.button.color.remove_color" })}>
                            <FormattedMessage id="app.button.color.remove_color" />
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                        {colorItems.map((item) => (
                            <ListBox.Item key={item.key} id={item.key} textValue={item.key}>
                                <span className="flex items-center gap-2">
                                    {item.icon}
                                    {item.label}
                                </span>
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                        ))}
                    </ListBox>
                </Select.Popover>
            </Select>
        ),
        [colorItems, intl],
    );

    return (
        <Modal.Backdrop isOpen={open} onOpenChange={handleOpenChange}>
            <Modal.Container>
                <Modal.Dialog className="w-150 max-w-150">
                    <Modal.CloseTrigger />
                    <Modal.Header>
                        <Modal.Heading>
                            <FormattedMessage
                                id={
                                    isEdit ? "app.home.calendar.modal.editTitle" : "app.home.calendar.modal.createTitle"
                                }
                            />
                        </Modal.Heading>
                    </Modal.Header>
                    <Modal.Body>
                        <form
                            id="calendar-event-form"
                            onSubmit={handleSubmit(onFinish)}
                            className="flex flex-col gap-4"
                        >
                            <Controller name="title" control={control} rules={titleRules} render={renderTitle} />
                            <Controller name="eventDate" control={control} rules={dateRules} render={renderEventDate} />
                            <Controller name="type" control={control} render={renderType} />
                            <Controller name="assigneeId" control={control} render={renderAssignee} />
                            <Controller name="contactId" control={control} render={renderContact} />
                            <Controller name="notes" control={control} render={renderNotes} />
                            <Controller name="color" control={control} render={renderColor} />
                        </form>
                    </Modal.Body>
                    <Modal.Footer className="justify-between">
                        {isEdit ? (
                            <PopconfirmHero
                                onConfirm={handleDelete}
                                title={DELETE_CONFIRM_TITLE}
                                trigger={DELETE_TRIGGER}
                            />
                        ) : (
                            <span />
                        )}
                        <Button type="submit" form="calendar-event-form">
                            <FormattedMessage id="app.button.save" />
                        </Button>
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    );
};

export const CalendarEventFormModal = React.memo(CalendarEventFormModalFC);
